import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  Alert,
} from "reactstrap";
import Loader from "../../../modules/loader";
import { postMethod } from "../../../../_services/_postMethod";

const BulkProductImport = ({ isOpen, toggle, branchId, userId }) => {
  const [excelData, setExcelData] = useState([]); // Parsed Excel data
  const [previewData, setPreviewData] = useState([]); // Data for preview table
  const [uploadResult, setUploadResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);

  // 📂 Handle Excel file upload
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    resetState(false);
    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheet];
        const parsed = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        if (parsed.length === 0) {
          setMessage("⚠️ No data found in the uploaded file.");
        } else {
          setExcelData(parsed);
          setMessage("✅ File uploaded successfully. Click 'Preview' to review data.");
        }
      } catch (error) {
        console.error("File parsing error:", error);
        setMessage("❌ Failed to read Excel file. Please try again.");
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // 👁️ Show preview table
  const handlePreview = () => {
    if (excelData.length === 0) {
      setMessage("⚠️ Please upload a valid Excel file before previewing.");
      return;
    }
    setPreviewData(excelData.slice(0, 5)); // show first 5 rows
    setIsPreviewVisible(true);
  };

  // 📥 Download sample Excel template
  const handleDownloadSample = () => {
    const link = document.createElement("a");
    link.href = `${process.env.PUBLIC_URL}/product.xlsx`;
    link.download = "product_sample.xlsx";
    link.click();
  };

  // 🚀 Upload Excel data to backend
  const handleUpload = async () => {
    if (excelData.length === 0) {
      setMessage("⚠️ Please select a valid Excel file before uploading.");
      return;
    }

    setLoading(true);
    setMessage("⏳ Uploading data to server...");
    setUploadResult(null);

    try {
      const payload = { products: excelData };
     
      const res = await postMethod(`api/masters/product/bulk_product`, payload);
      const apiData = res?.data?.data?.[0];

      if (apiData) {
        setUploadResult(apiData.details);
        setMessage(
          `✅ ${apiData.inserted} inserted | ⚠️ ${apiData.skipped} skipped | ❌ ${apiData.failed} failed`
        );
      } else {
        setMessage(`✅ Upload complete`);
         setTimeout(() => {
      window.location.reload(); // 🔄 refresh after 2 seconds
    }, 2000);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setMessage("❌ Upload failed. Please check console or try again.");
    } finally {
      setLoading(false);
    }
  };

  // 🧹 Reset component state
  const resetState = (closeModal = true) => {
    setExcelData([]);
    setPreviewData([]);
    setUploadResult(null);
    setMessage("");
    setIsPreviewVisible(false);
    if (closeModal) toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={() => resetState(true)} size="lg" centered>
      <ModalHeader toggle={() => resetState(true)}>Bulk Product Import</ModalHeader>

      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {loading && <Loader />}

        {/* File Controls */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            disabled={loading}
          />
          <Button
            color="secondary"
            onClick={handlePreview}
            disabled={!excelData.length || loading}
            style={{marginRight:"0.5rem"}}
          >
            <i className="flaticon2-search"></i> Preview
          </Button>
          <Button color="info" onClick={handleDownloadSample}>
            <i className="flaticon2-download"></i> Download Sample
          </Button>
        </div>

        {/* Info / Status Message */}
        {message && <Alert color="info">{message}</Alert>}

        {/* Preview Table */}
        {isPreviewVisible && previewData.length > 0 && (
          <div className="table-responsive">
            <Table bordered size="sm" className="mb-2" style={{ tableLayout: "fixed" }}>
              <thead>
                <tr>
                  {Object.keys(previewData[0]).map((key, idx) => (
                    <th
                      key={idx}
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "150px",
                        fontSize: "13px",
                      }}
                    >
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewData.map((row, i) => (
                  <tr key={i}>
                    {Object.values(row).map((val, j) => (
                      <td
                        key={j}
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "150px",
                          fontSize: "13px",
                          verticalAlign: "middle",
                        }}
                      >
                        {val?.toString()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
            <small className="text-muted">Showing first 5 rows</small>
          </div>
        )}

        {/* Upload Result Summary */}
        {uploadResult && (
          <div className="mt-3">
            <h6>Upload Summary:</h6>
            <ul>
              <li>✅ Inserted: {uploadResult.inserted?.length || 0}</li>
              <li>⚠️ Skipped: {uploadResult.skipped?.length || 0}</li>
              <li>❌ Failed: {uploadResult.failed?.length || 0}</li>
            </ul>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button
          color="primary"
          onClick={handleUpload}
          disabled={loading || !excelData.length}
        >
          {loading ? "Uploading..." : "Upload"}
        </Button>
        <Button color="secondary"  onClick={() => {
          resetState(true);
          window.location.reload();
        }}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BulkProductImport;
